import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, Plus, FileText, Layers, LogOut, Trash2, ArrowUpRight, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_COLOR = {
  draft: "text-zinc-500",
  analyzed: "text-cyan-400",
  reported: "text-[#d4af37]",
};

const PLAN_LABEL = { free: "Free", pro: "Pro", bisnis: "Bisnis", admin: "Admin" };
const PLAN_BADGE = {
  free: "border-zinc-700 text-zinc-400",
  pro: "border-cyan-500/40 text-cyan-300 bg-cyan-500/10",
  bisnis: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/10",
  admin: "border-[#d4af37]/60 text-[#d4af37] bg-[#d4af37]/10",
};

function PlanBadge({ billing, t }) {
  if (!billing) return null;
  const plan = billing.plan || "free";
  const limit = billing.limits?.analyses ?? 0;
  const used = billing.usage?.analyses ?? 0;
  const remaining = Math.max(0, limit - used);
  const unlimited = limit > 100000;
  return (
    <div data-testid="plan-badge" className="flex items-center gap-2">
      <span className={`font-mono-ui text-[10px] uppercase tracking-wider border rounded-full px-2.5 py-1 ${PLAN_BADGE[plan] || PLAN_BADGE.free}`}>
        {PLAN_LABEL[plan] || plan}
      </span>
      <span
        data-testid="quota-indicator"
        className={`font-mono-ui text-[11px] ${!unlimited && remaining === 0 ? "text-rose-400" : "text-zinc-500"}`}
      >
        {unlimited ? t("dash.quotaUnlimited") : t("dash.quotaLeft", { remaining, limit })}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [cases, setCases] = useState(null);
  const [billing, setBilling] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const STATUS_LABEL = {
    draft: t("dash.statusDraft"),
    analyzed: t("dash.statusAnalyzed"),
    reported: t("dash.statusReported"),
  };

  const load = useCallback(
    () => api.get("/cases").then((r) => setCases(r.data)).catch((e) => toast.error(apiErr(e))),
    []
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/cases", { title, description: desc });
      setOpen(false); setTitle(""); setDesc("");
      nav(`/cases/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setCreating(false); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(t("dash.deleteConfirm"))) return;
    try { await api.delete(`/cases/${id}`); load(); toast.success(t("dash.deleted")); }
    catch (e2) { toast.error(apiErr(e2)); }
  };

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0a0c]/80 border-b border-white/5">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Scale className="h-5 w-5 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-mono-ui text-sm tracking-[0.2em] uppercase">Lexiqa</span>
          </div>
          <div className="flex items-center gap-4">
            <PlanBadge billing={billing} t={t} />
            <LanguageToggle />
            <a data-testid="dashboard-manual-link"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/manual.pdf`}
              target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-zinc-400 hover:text-zinc-100 transition-colors">
              <BookOpen className="h-4 w-4" /> {t("common.guide")}
            </a>
            <a data-testid="pricing-link" href="/pricing"
              className="font-mono-ui text-[11px] uppercase tracking-wider text-[#d4af37] hover:text-[#e6c04d] transition-colors">{t("common.upgrade")}</a>
            <span className="hidden sm:block font-mono-ui text-xs text-zinc-500">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-zinc-500 hover:text-zinc-200 transition-colors"
              title={t("common.logout")}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-500">{t("dash.workspace")}</p>
            <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl font-light text-zinc-50">{t("dash.title")}</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-case-btn" className="bg-zinc-50 text-zinc-950 hover:bg-white rounded-full h-11 px-5">
                <Plus className="h-4 w-4 mr-1.5" /> {t("dash.newCase")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121216] border-zinc-800 text-zinc-100 w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl font-light">{t("dash.newCase")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <Input
                  data-testid="case-title-input"
                  placeholder={t("dash.titlePh")}
                  value={title} onChange={(e) => setTitle(e.target.value)}
                  className="bg-[#0a0a0c] border-zinc-800"
                />
                <Textarea
                  data-testid="case-desc-input"
                  placeholder={t("dash.descPh")}
                  value={desc} onChange={(e) => setDesc(e.target.value)}
                  className="bg-[#0a0a0c] border-zinc-800 min-h-[90px]"
                />
              </div>
              <DialogFooter>
                <Button data-testid="create-case-confirm-btn" onClick={create} disabled={creating}
                  className="bg-zinc-50 text-zinc-950 hover:bg-white">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : t("dash.create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Grid */}
        <div className="mt-10">
          {cases === null ? (
            <div className="text-zinc-600 font-mono-ui text-sm">{t("dash.loading")}</div>
          ) : cases.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-800 p-16 text-center">
              <FileText className="mx-auto h-8 w-8 text-zinc-700" strokeWidth={1.5} />
              <p className="mt-4 text-zinc-400">{t("dash.empty")}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cases.map((c, i) => (
                <motion.button
                  key={c.id}
                  data-testid={`case-card-${c.id}`}
                  onClick={() => nav(`/cases/${c.id}`)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="group text-left rounded-xl border border-[#2a2a35] bg-[#18181d] p-5 hover:border-zinc-600 hover:bg-[#202027] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <span className={`font-mono-ui text-[10px] uppercase tracking-wider ${STATUS_COLOR[c.status]}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2
                        onClick={(e) => remove(e, c.id)}
                        className="h-3.5 w-3.5 text-zinc-500 hover:text-rose-400"
                      />
                      <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                    </div>
                  </div>
                  <h3 className="mt-3 font-serif-display text-xl font-normal text-zinc-100 leading-snug line-clamp-2">
                    {c.title}
                  </h3>
                  {c.description && (
                    <p className="mt-1.5 text-sm text-zinc-500 line-clamp-2">{c.description}</p>
                  )}
                  <div className="mt-4 flex items-center gap-4 font-mono-ui text-[11px] text-zinc-500">
                    <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {c.document_count} {t("unit.docs")}</span>
                    <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {c.item_count} {t("unit.items")}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
